import type { CSSProperties } from 'react';
import type React from 'react';
import { useState } from 'react';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { STEP_ORDER } from '../context/constants';
import { useGoToPrevStep, useSelector } from '../context/hooks';
import {
	selectEstimatedUsageRange,
	selectIncludedAllowance,
	selectMeter,
	selectShareableQuery,
} from '../context/selectors';
import { toQueryString } from '../util/url';

import { EstimateDisclaimer } from './estimate-disclaimer';
import { METER_LABELS_LOWER } from './estimate-step';
import { FeedbackButtons, type FeedbackVote } from './feedback-buttons';
import { ResultsCard } from './results-card';
import { ShareButton } from './share-button';
import { Stepper } from './stepper';
import { METER_LABELS } from './strings';

import type { Step } from '../context/types';

// ─────────────────────────────────────────────────────────────────────────
// Summary step layout (per Figma node 1103:32406):
//
//   Header (80px above H3):
//     H3: "Review usage estimate and compare"
//     Body Large: dynamic subtitle
//
//   ┌───────────────────────────────────┬────────────────────────────┐
//   │  ResultsCard: "Your org needs"    │                            │
//   │                                   │                            │
//   │  ResultsCard: "What's covered"    │  [ Price / Product card    │
//   │                                   │    placeholder ]           │
//   │  HowYourTeamsMightUse             │                            │
//   │  (value cards WITHOUT icons)      │                            │
//   └───────────────────────────────────┴────────────────────────────┘
//
//   FeedbackButtons (centered)
//   [Back]  [Copy URL to share]
//   Non-binding-estimate disclaimer
//
// Layout parity with the Estimate step (same header treatment, same
// feedback wrapper, same bottom action row + disclaimer) so navigating
// between the two steps feels consistent.
// ─────────────────────────────────────────────────────────────────────────

export const SummaryStep = () => {
	const meter = useSelector(selectMeter);
	const meterLower = METER_LABELS_LOWER[meter];
	const goToPrev = useGoToPrevStep();

	// Feedback vote is component-local for now — mirrors the Estimate
	// step's treatment. A follow-up ticket will wire this into analytics
	// and persist the preference.
	const [vote, setVote] = useState<FeedbackVote>(null);

	// Build a real, valid href for the Back link that points at the same
	// calculator URL with `step` set to the previous step. Enables the
	// anchor to be bookmarkable / right-clickable / openable in a new
	// tab. The inline onClick prevents the full page reload and drives
	// the calculator state machine instead.
	const shareableQuery = useSelector(selectShareableQuery);
	const currentStep = useSelector((ctx) => ctx.step);
	const prevStep: Step | undefined = STEP_ORDER[STEP_ORDER.indexOf(currentStep) - 1];
	const prevStepHref = prevStep ? `?${toQueryString({ ...shareableQuery, step: prevStep })}` : '?';

	return (
		<>
			<Stepper />
			{/*
			 * Summary step header per Figma node 1103:32406:
			 *   H3 (Charlie Display Bold 32/38) "Review usage estimate and compare"
			 *   Body Large (Charlie Text Regular 20/28) subtitle
			 *
			 * 80px padding above the H3 (40 base + 40 added per design
			 * refinement) and 40px below the subtitle.
			 *
			 * Wrapped in TypographyStyles so raw `h3` inherits the WAC
			 * design-system Charlie font stack (same pattern used on the
			 * Estimate step).
			 */}
			<TypographyStyles>
				<div style={pageHeaderStyle}>
					<h3 style={pageTitleStyle}>Review usage estimate and compare</h3>
					<p style={pageSubtitleStyle}>
						Based on what you&rsquo;ve told us, here&rsquo;s a summary of how many {meterLower} you
						need, and how you can use them.
					</p>
				</div>
			</TypographyStyles>
			<div style={twoColumnRowStyle}>
				<div style={leftColumnStyle}>
					<OrgNeedsResultsCard />
					<CoveredResultsCard />
					<HowYourTeamsMightUse />
				</div>
				<div style={rightColumnStyle}>
					<PriceCardPlaceholder />
				</div>
			</div>

			{/*
			 * Feedback buttons — sit between the content and the Back/Share
			 * row per Figma node 1103:32406.
			 */}
			<div style={feedbackWrapperStyle}>
				<FeedbackButtons vote={vote} onVote={setVote} />
			</div>
			{/*
			 * Bottom action row per Figma:
			 *   [Back]  [Copy URL to share]
			 *
			 * Both buttons sit side-by-side, vertically centered.
			 * Back uses the design-library `<Link>` treatment; Copy URL
			 * uses the design-library `<Button variant="primary">` via
			 * `<ShareButton />`. See the extended comment on Estimate
			 * step for the anchor-as-button rationale + eslint disables.
			 */}
			<div style={bottomActionRowStyle}>
				{/* eslint-disable-next-line jsx-a11y/anchor-is-valid, @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
				<Link
					type="primary"
					size="md"
					text="Back"
					ariaLabel="Back"
					href={prevStepHref}
					onClick={(e: React.MouseEvent<Element>) => {
						e.preventDefault();
						goToPrev();
					}}
					showArrow={false}
					showUnderline={false}
				/>
				<ShareButton />
			</div>
			<EstimateDisclaimer />
		</>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Left-column: ResultsCard for "Your organization needs"
// ─────────────────────────────────────────────────────────────────────────
const OrgNeedsResultsCard = () => {
	const meter = useSelector(selectMeter);
	const { low, high } = useSelector(selectEstimatedUsageRange);
	return (
		<ResultsCard
			eyebrow="YOUR ORGANIZATION NEEDS"
			headline={`${low.toLocaleString('en-US')}–${high.toLocaleString('en-US')}`}
			headlineSubtext={`${METER_LABELS[meter]} per month`}
			testId="summary-org-needs"
		/>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Left-column: ResultsCard for "What's covered"
// ─────────────────────────────────────────────────────────────────────────
const CoveredResultsCard = () => {
	const meter = useSelector(selectMeter);
	const included = useSelector(selectIncludedAllowance);
	return (
		<ResultsCard
			eyebrow="WHAT'S COVERED"
			headline={included.toLocaleString('en-US')}
			headlineSubtext={`${METER_LABELS[meter]} included, monthly`}
			testId="summary-covered"
		/>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Left-column: "How your teams might use" — value cards WITHOUT icons
// ─────────────────────────────────────────────────────────────────────────
const HowYourTeamsMightUse = () => {
	const meter = useSelector(selectMeter);
	const meterLower = METER_LABELS_LOWER[meter];
	return (
		<div style={valueCardsPanelStyle}>
			<p style={valueCardsEyebrowStyle}>How your teams might use {meterLower}</p>
			<div style={valuePropStackStyle}>
				<div style={valuePropItemStyle}>
					<p style={valuePropTitleStyle}>Maximize your ROI</p>
					<p style={valuePropDescriptionStyle}>
						Value prop 1 for {meterLower}. Description of how this benefit applies to your
						team&rsquo;s usage.
					</p>
				</div>
				<div style={valuePropItemStyle}>
					<p style={valuePropTitleStyle}>Plan with confidence</p>
					<p style={valuePropDescriptionStyle}>
						Value prop 2 for {meterLower}. Description of how this benefit applies to your
						team&rsquo;s usage.
					</p>
				</div>
				<div style={valuePropItemStyle}>
					<p style={valuePropTitleStyle}>Unlock more with less</p>
					<p style={valuePropDescriptionStyle}>
						Value prop 3 for {meterLower}. Description of how this benefit applies to your
						team&rsquo;s usage.
					</p>
				</div>
			</div>
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────
// Right-column: Price / Product card placeholder (GWP-12557)
// ─────────────────────────────────────────────────────────────────────────
const PriceCardPlaceholder = () => (
	<div style={placeholderCardStyle} data-testid="summary-price-card-placeholder">
		<p style={placeholderLabelStyle}>Price / Product Card</p>
		<p style={placeholderNoteStyle}>Placeholder — see GWP-12557</p>
	</div>
);

// ─────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────

// Header per Figma node 1103:32406 — 80px above the H3 (40px base + 40
// added per design refinement) and 40px below the subtitle.
const pageHeaderStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '8px',
	paddingBlockStart: '80px',
	paddingBlockEnd: '40px',
};

const pageTitleStyle: CSSProperties = {
	// TypographyStyles maps raw `h3` to Charlie Display Bold 32/38.
	margin: 0,
};

const pageSubtitleStyle: CSSProperties = {
	// Body Large: Charlie Text Regular 20/28.
	margin: 0,
	fontSize: '20px',
	lineHeight: '28px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

// Two-column layout — collapses to single column below ~832px via
// `repeat(auto-fit, minmax(400px, 1fr))`. Matches Estimate step.
const twoColumnRowStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
	gap: '32px',
	alignItems: 'start',
};

const leftColumnStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
};

const rightColumnStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
};

// Value cards panel (mirrors Estimate step's HowYourTeamsMightUse).
const valueCardsPanelStyle: CSSProperties = {
	backgroundColor: wacToken('color.neutral.light100'),
	borderRadius: '20px',
	padding: '20px 20px 32px 20px',
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
};

const valueCardsEyebrowStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '18px',
	fontWeight: 400,
	textTransform: 'uppercase',
	letterSpacing: 0,
	color: wacToken('color.neutral.light1100'),
};

const valuePropStackStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
};

const valuePropItemStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: 0,
};

const valuePropTitleStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

const valuePropDescriptionStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

// Price / Product card placeholder — dashed border, centered content.
const placeholderCardStyle: CSSProperties = {
	padding: '40px 24px',
	borderRadius: '20px',
	border: `2px dashed ${wacToken('color.neutral.light400')}`,
	backgroundColor: wacToken('color.neutral.light100'),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	minHeight: '320px',
	textAlign: 'center',
};

const placeholderLabelStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '24px',
	lineHeight: '30px',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

const placeholderNoteStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '20px',
	color: wacToken('color.neutral.light800'),
};

// Feedback wrapper — mirrors Estimate step (56px above, 44px below,
// horizontally centered).
const feedbackWrapperStyle: CSSProperties = {
	display: 'flex',
	justifyContent: 'center',
	paddingBlockStart: '56px',
	paddingBlockEnd: '44px',
};

// Bottom action row — Back link + Share button, side-by-side,
// vertically centered. Mirrors Estimate step's `bottomActionRowStyle`.
const bottomActionRowStyle: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '12px',
};
